/* eslint-disable max-depth */
import React, { useRef } from 'react';
import merge from 'lodash/merge';
import { FormState, FormFieldAction, FormProps, FormInterface, FormItemInterface } from '../type/form';
import { setObj, omit, getObj } from '../util';

interface FormContextInterface {
  state: FormState;
  dispatch: React.Dispatch<FormFieldAction>;
}

export const FormContext = React.createContext<FormContextInterface>({
  state: { values: {}, errors: {}, inputs: {}, groups: {} },
  dispatch: () => {
    // empty
  },
});

// eslint-disable-next-line max-lines-per-function
const Form: React.FC<FormProps> = (props) => {
  const {
    initialState = {},
    children,
    instance = () => {
      // empty
    },
  } = props;
  const initialProps: FormState = {
    values: {
      ...initialState,
    },
    errors: {},
    inputs: {},
    groups: {},
  };

  const curVersion = useRef(0);

  const reducer = (preState: FormState, action: FormFieldAction) => {
    const { type = 'change', name = '', payload = {} } = action;
    let properties: string[] = [];
    switch (type) {
      case 'error':
        setObj(preState.errors, name, payload);
        break;
      case 'bindInput':
        const formItemInterface: FormItemInterface = payload;
        if (!getObj(preState.errors, name)) {
          setObj(preState.errors, name, []);
        }
        if (!getObj(preState.values, name)) {
          setObj(preState.values, name, '');
        }
        const { group } = formItemInterface;
        if (group) {
          preState.groups[group] = preState.groups[group] || {};
          preState.groups[group][name] = formItemInterface;
        }
        preState.inputs[name] = payload;
        return preState;
      case 'unmount':
        properties = name.split('.');
        if (properties.length > 1) {
          const result = /^([a-zA-Z]{1,})\[(\d{1,})\]/.exec(properties[properties.length - 2]);
          if (result) {
            // username[xxxx].xxx or username.xxxx[xxxx].xxxx
            const name1 = result[1];
            const index = parseInt(result[2], 0);
            const propertyName = properties
              .splice(0, properties.length - 2)
              .concat(name1)
              .join('.');
            const array = getObj(preState.errors, propertyName);
            if (array && array.splice) {
              array.splice(index, 1);
              if (payload) {
                delete preState.groups[payload.group];
              }
            }
          }
        }
        delete preState.inputs[name];
        omit(preState.errors, name);
        if (payload) {
          delete preState.groups[payload.group];
        }
        omit(preState.values, name);
        break;
      case 'reset':
        preState.values = {};
        preState.errors = {};
        curVersion.current += 1;
        break;
      case 'merge':
        preState.values = merge(preState.values, payload);
        curVersion.current += 1;

        break;
      case 'change':
      default:
        setObj(preState.values, name, payload);
        curVersion.current += 1;
        break;
    }
    return { ...preState };
  };

  const [state, dispatch] = React.useReducer(reducer, initialProps);
  const formInterface: FormInterface = {
    getValues() {
      return { ...state.values };
    },
    getErrors() {
      return { ...state.errors };
    },
    merge(values) {
      dispatch({ type: 'merge', payload: values, name: '' });
    },
    reset() {
      dispatch({ type: 'reset' });
    },
    async submit() {
      const { inputs } = state;
      const names = Object.getOwnPropertyNames(state.inputs);
      const promises = names.map((name) => inputs[name].validate('submit'));
      const result = await Promise.all(promises);
      if (
        result.some((item, index) => {
          if (!item) {
            inputs[names[index]].focus();
          }
          return item === false;
        })
      ) {
        return false;
      } else {
        return true;
      }
    },
    // 在每次修改时判断是否符合提交要求
    async onChange(formState) {
      const { values, inputs } = formState;
      const names = Object.getOwnPropertyNames(formState.inputs);
      const promises = names.map((name) => inputs[name].validate('isEdit'));
      const result = await Promise.all(promises);
      let flag = true;
      for (const item of result) {
        if (!item) {
          flag = false;
          break;
        }
      }

      if (values.reportFormSendBtn === flag) {
        return;
      }

      dispatch({ type: 'change', payload: flag, name: 'reportFormSendBtn' });
    },
  };

  React.useEffect(() => {
    if (formInterface.onChange) {
      formInterface.onChange({
        values: { ...state.values },
        inputs: { ...state.inputs },
      });
    }
  }, [curVersion.current]);

  instance(formInterface);

  return (
    <FormContext.Provider
      value={{
        state,
        dispatch,
      }}
    >
      {children}
    </FormContext.Provider>
  );
};

export default Form;
